import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import { useCompany } from "../../context/CompanyContext";
import { showToast } from "../../utils/customToast";
import "./VoucherRegister.css";
import { MasterTableSkeleton, VoucherModalSkeleton } from "../Skeleton";

const VOUCHER_TYPE_COLORS = {
  1: "vch_badge_1", // Receipt - Blue
  2: "vch_badge_2", // Payment - Amber
  3: "vch_badge_3", // Journal - Purple
  4: "vch_badge_4", // Contra - Slate
  5: "vch_badge_5", // Sales - Emerald
  6: "vch_badge_6", // Purchase - Teal
};

export default function VoucherRegister() {
  const navigate = useNavigate();
  const { selectedCompany } = useCompany();
  const companyId = selectedCompany?.id || localStorage.getItem("companyId") || "4211";

  const BASE_API_URL = process.env.REACT_APP_BASE_API_URL || "http://localhost:5000/api";

  const [vouchers, setVouchers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [voucherTypes, setVoucherTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Filters
  const [activeVType, setActiveVType] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Modal State for Voucher Details
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [voucherDetails, setVoucherDetails] = useState([]);
  const [isDetailLoading, setIsDetailLoading] = useState(false);

  // Modal State for Generate from Invoice
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [invoiceIdInput, setInvoiceIdInput] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  // Fetch Voucher Types
  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const res = await axios.get(`${BASE_API_URL}/vouchers/types`);
        if (res.data?.success) {
          setVoucherTypes(res.data.data || []);
        }
      } catch (err) {
        console.error("Error loading voucher types:", err);
      }
    };
    fetchTypes();
  }, [BASE_API_URL]);

  // Fetch Vouchers list
  const fetchVouchers = useCallback(async () => {
    if (!companyId) return;
    setIsLoading(true);
    try {
      const params = {};
      if (activeVType) params.vtype = activeVType;
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
      if (searchTerm) params.search = searchTerm;

      const res = await axios.get(`${BASE_API_URL}/vouchers/${companyId}`, { params });
      if (res.data?.success) {
        setVouchers(res.data.data || []);
        setSummary(res.data.summary || null);
      } else {
        setVouchers([]);
        setSummary(null);
      }
    } catch (err) {
      console.error("Failed to load vouchers:", err);
      showToast.error("Failed to load vouchers from server");
    } finally {
      setIsLoading(false);
    }
  }, [BASE_API_URL, companyId, activeVType, fromDate, toDate, searchTerm]);

  useEffect(() => {
    fetchVouchers();
  }, [fetchVouchers]);

  // Open voucher detail modal and fetch double-entry items
  const handleViewDetails = async (voucherId) => {
    setIsDetailLoading(true);
    try {
      const res = await axios.get(`${BASE_API_URL}/vouchers/details/${voucherId}`);
      if (res.data?.success) {
        setSelectedVoucher(res.data.voucher);
        setVoucherDetails(res.data.details || []);
      } else {
        showToast.error(res.data?.message || "Could not retrieve voucher details");
      }
    } catch (err) {
      console.error("Error fetching voucher detail:", err);
      showToast.error("Failed to fetch voucher items");
    } finally {
      setIsDetailLoading(false);
    }
  };

  // Generate voucher from invoice handler
  const handleGenerateFromInvoice = async (e) => {
    e.preventDefault();
    if (!invoiceIdInput.trim()) {
      showToast.warn("Please enter a valid Invoice ID");
      return;
    }

    setIsGenerating(true);
    try {
      const userId = localStorage.getItem("userId") || 1;
      const res = await axios.post(`${BASE_API_URL}/vouchers/generate-from-invoice`, {
        compId: Number(companyId),
        invoiceId: Number(invoiceIdInput.trim()),
        userId: Number(userId),
      });

      if (res.data?.success) {
        showToast.success(res.data.message || "Vouchers generated successfully!");
        setGenerateModalOpen(false);
        setInvoiceIdInput("");
        fetchVouchers();
      } else {
        showToast.error(res.data?.message || "Failed to generate vouchers");
      }
    } catch (err) {
      console.error("Error generating vouchers:", err);
      showToast.error(err.response?.data?.message || "Failed to execute voucher generation procedure");
    } finally {
      setIsGenerating(false);
    }
  };

  // Export to Excel
  const handleExportExcel = () => {
    if (!vouchers.length) {
      showToast.warn("No voucher records to export");
      return;
    }

    const dataRows = vouchers.map((v, idx) => ({
      "S.No": idx + 1,
      "Voucher No": v.VoucherNo || v.VoucherID,
      "Date": v.VoucherDate ? new Date(v.VoucherDate).toLocaleDateString("en-IN") : "-",
      "Voucher Type": v.VTypeName || `Type ${v.VType}`,
      "Ref / Invoice No": v.ReferenceNo || "-",
      "Particulars / Summary": v.SummaryParticulars || "-",
      "Amount (Debit)": Number(v.TotalDebit || v.TotalAmount || 0),
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Vouchers");
    XLSX.writeFile(workbook, `Voucher_Register_${companyId}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    showToast.success("Exported vouchers to Excel");
  };

  // Reset filters
  const handleResetFilters = () => {
    setActiveVType("");
    setFromDate("");
    setToDate("");
    setSearchTerm("");
  };

  // Format currency in Indian format
  const formatINR = (val) => {
    return Number(val || 0).toLocaleString("en-IN", {
      maximumFractionDigits: 2,
      minimumFractionDigits: 2,
    });
  };

  return (
    <div className="voucher_page_wrapper">
      {/* Top Header & Breadcrumb */}
      <div className="voucher_header_bar">
        <div className="voucher_breadcrumb">
          <span className="bc_prefix">Accounts /</span> Day Book & Voucher Register
        </div>
        <div className="voucher_header_actions">
          <button
            type="button"
            className="vch_btn_export"
            onClick={handleExportExcel}
            title="Export list to Excel"
          >
            <i className="fa-solid fa-file-excel"></i> Export Excel
          </button>
          <button
            type="button"
            className="vch_btn_generate"
            onClick={() => setGenerateModalOpen(true)}
            title="Generate vouchers automatically from Invoice"
          >
            <i className="fa-solid fa-wand-magic-sparkles"></i> Post from Invoice
          </button>
        </div>
      </div>

      {/* Summary KPI Cards */}
      <div className="voucher_kpi_grid">
        <div className="vch_kpi_card vch_kpi_sales">
          <div className="vch_kpi_icon">
            <i className="fa-solid fa-chart-line"></i>
          </div>
          <div className="vch_kpi_info">
            <span className="vch_kpi_title">Total Sales (VType 5)</span>
            <span className="vch_kpi_val">₹{formatINR(summary?.totalSales)}</span>
          </div>
        </div>

        <div className="vch_kpi_card vch_kpi_journal">
          <div className="vch_kpi_icon">
            <i className="fa-solid fa-book-journal-whills"></i>
          </div>
          <div className="vch_kpi_info">
            <span className="vch_kpi_title">Total Journal (VType 3)</span>
            <span className="vch_kpi_val">₹{formatINR(summary?.totalJournal)}</span>
          </div>
        </div>

        <div className="vch_kpi_card vch_kpi_receipts">
          <div className="vch_kpi_icon">
            <i className="fa-solid fa-hand-holding-dollar"></i>
          </div>
          <div className="vch_kpi_info">
            <span className="vch_kpi_title">Total Receipts (1)</span>
            <span className="vch_kpi_val">₹{formatINR(summary?.totalReceipts)}</span>
          </div>
        </div>

        <div className="vch_kpi_card vch_kpi_payments">
          <div className="vch_kpi_icon">
            <i className="fa-solid fa-credit-card"></i>
          </div>
          <div className="vch_kpi_info">
            <span className="vch_kpi_title">Total Payments (2)</span>
            <span className="vch_kpi_val">₹{formatINR(summary?.totalPayments)}</span>
          </div>
        </div>

        <div className="vch_kpi_card vch_kpi_balance">
          <div className="vch_kpi_icon">
            <i className="fa-solid fa-scale-balanced"></i>
          </div>
          <div className="vch_kpi_info">
            <span className="vch_kpi_title">Books Balance</span>
            <span className="vch_kpi_val">₹{formatINR(summary?.totalDebit)}</span>
            <span className="vch_balance_status">
              <i className="fa-solid fa-shield-check"></i> Dr = Cr Balanced
            </span>
          </div>
        </div>
      </div>

      {/* Filter and Search Controls */}
      <div className="voucher_controls_card">
        {/* VType Pill Tabs */}
        <div className="vch_type_tabs">
          <button
            type="button"
            className={`vch_tab_pill ${activeVType === "" ? "active" : ""}`}
            onClick={() => setActiveVType("")}
          >
            All Types <span className="vch_tab_count">{summary?.totalCount || vouchers.length}</span>
          </button>
          <button
            type="button"
            className={`vch_tab_pill ${activeVType === "5" ? "active" : ""}`}
            onClick={() => setActiveVType("5")}
          >
            Sales (5)
          </button>
          <button
            type="button"
            className={`vch_tab_pill ${activeVType === "3" ? "active" : ""}`}
            onClick={() => setActiveVType("3")}
          >
            Journal (3)
          </button>
          <button
            type="button"
            className={`vch_tab_pill ${activeVType === "1" ? "active" : ""}`}
            onClick={() => setActiveVType("1")}
          >
            Receipts (1)
          </button>
          <button
            type="button"
            className={`vch_tab_pill ${activeVType === "2" ? "active" : ""}`}
            onClick={() => setActiveVType("2")}
          >
            Payments (2)
          </button>
          <button
            type="button"
            className={`vch_tab_pill ${activeVType === "6" ? "active" : ""}`}
            onClick={() => setActiveVType("6")}
          >
            Purchase (6)
          </button>
          <button
            type="button"
            className={`vch_tab_pill ${activeVType === "4" ? "active" : ""}`}
            onClick={() => setActiveVType("4")}
          >
            Contra (4)
          </button>
        </div>

        {/* Date Filters & Search Bar */}
        <div className="vch_filters_row">
          <div className="vch_date_filters">
            <div className="vch_filter_item">
              <label className="vch_filter_label">From Date:</label>
              <input
                type="date"
                className="vch_input_date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>

            <div className="vch_filter_item">
              <label className="vch_filter_label">To Date:</label>
              <input
                type="date"
                className="vch_input_date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>

            <div className="vch_search_wrapper">
              <i className="fa-solid fa-magnifying-glass vch_search_icon"></i>
              <input
                type="text"
                className="vch_search_input"
                placeholder="Search voucher #, ref #, narration..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="vch_filter_actions">
            <button
              type="button"
              className="vch_btn_apply"
              onClick={fetchVouchers}
              title="Apply Filters"
            >
              <i className="fa-solid fa-filter"></i> Apply
            </button>
            <button
              type="button"
              className="vch_btn_reset"
              onClick={handleResetFilters}
              title="Reset Filters"
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Vouchers Table */}
      <div className="vch_table_card">
        <div className="vch_table_container">
          {isLoading ? (
            <MasterTableSkeleton columns={7} rows={6} hasToolbar={false} />
          ) : vouchers.length === 0 ? (
            <div className="vch_empty_state">
              <div className="vch_empty_icon">
                <i className="fa-regular fa-folder-open"></i>
              </div>
              <h4>No vouchers found</h4>
              <p>Try adjusting your search criteria or post vouchers from an invoice.</p>
            </div>
          ) : (
            <table className="vch_table">
              <thead>
                <tr>
                  <th style={{ width: "50px", textAlign: "center" }}>#</th>
                  <th>Voucher No</th>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Reference / Invoice</th>
                  <th>Particulars / Narration</th>
                  <th style={{ textAlign: "right" }}>Total Amount (₹)</th>
                  <th style={{ width: "70px", textAlign: "center" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {vouchers.map((vch, index) => {
                  const badgeClass = VOUCHER_TYPE_COLORS[vch.VType] || "vch_badge_4";
                  const formattedDate = vch.VoucherDate
                    ? new Date(vch.VoucherDate).toLocaleDateString("en-IN")
                    : "-";
                  const displayAmount = vch.TotalDebit || vch.TotalAmount || 0;

                  return (
                    <tr key={vch.VoucherID || index}>
                      <td style={{ textAlign: "center", color: "#94a3b8" }}>{index + 1}</td>
                      <td>
                        <span
                          className="vch_no_link"
                          onClick={() => handleViewDetails(vch.VoucherID)}
                          title="Click to view voucher detail"
                        >
                          <i className="fa-solid fa-file-invoice"></i> {vch.VoucherNo || vch.VoucherID}
                        </span>
                      </td>
                      <td>{formattedDate}</td>
                      <td>
                        <span className={`vch_badge ${badgeClass}`}>
                          {vch.VTypeName || `VType ${vch.VType}`}
                        </span>
                      </td>
                      <td>
                        {vch.ReferenceNo ? (
                          <span className="vch_ref_badge">
                            <i className="fa-solid fa-receipt"></i> Inv #{vch.ReferenceNo}
                          </span>
                        ) : (
                          <span style={{ color: "#94a3b8" }}>-</span>
                        )}
                        {vch.CircularRefNo && (
                          <span
                            className="vch_ref_badge"
                            style={{ marginLeft: 6, background: "#f5f3ff", color: "#7c3aed", borderColor: "#ddd6fe" }}
                            title="Linked Sales Voucher ID"
                          >
                            <i className="fa-solid fa-link"></i> Sales #{vch.CircularRefNo}
                          </span>
                        )}
                      </td>
                      <td style={{ maxWidth: "340px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {vch.SummaryParticulars || "-"}
                      </td>
                      <td style={{ textAlign: "right", fontWeight: 700 }}>
                        ₹{formatINR(displayAmount)}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <div className="d-flex align-items-center justify-content-center gap-1">
                          <button
                            type="button"
                            className="vch_action_btn"
                            onClick={() => handleViewDetails(vch.VoucherID)}
                            title="View Double-Entry Breakdown"
                          >
                            <i className="fa-solid fa-eye"></i>
                          </button>
                          <button
                            type="button"
                            className="vch_action_btn"
                            onClick={() =>
                              navigate(
                                `/transactions/modify-voucher?voucherId=${
                                  vch.VoucherID || vch.voucherId
                                }`
                              )
                            }
                            title="Modify this Voucher in Transactions"
                            style={{ color: "#d97706" }}
                          >
                            <i className="fa-solid fa-pen-to-square"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Double-Entry Voucher Detail Modal */}
      {(selectedVoucher || isDetailLoading) && (
        <div className="vch_modal_backdrop" onClick={() => setSelectedVoucher(null)}>
          <div className="vch_modal_card" onClick={(e) => e.stopPropagation()}>
            <div className="vch_modal_header">
              <div className="vch_modal_title">
                <i className="fa-solid fa-scale-balanced" style={{ color: "var(--vch-accent)" }}></i>
                <span>Voucher Details: {selectedVoucher?.VoucherNo || selectedVoucher?.VoucherID}</span>
                {selectedVoucher && (
                  <span className={`vch_badge ${VOUCHER_TYPE_COLORS[selectedVoucher.VType] || ""}`}>
                    {selectedVoucher.VTypeName || `Type ${selectedVoucher.VType}`}
                  </span>
                )}
              </div>
              <button
                type="button"
                className="vch_modal_close"
                onClick={() => setSelectedVoucher(null)}
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>

            <div className="vch_modal_body">
              {isDetailLoading ? (
                <VoucherModalSkeleton />
              ) : (
                <>
                  {/* Meta Summary */}
                  <div className="vch_meta_summary">
                    <div className="vch_meta_item">
                      <span className="vch_meta_label">Voucher Number</span>
                      <span className="vch_meta_val">{selectedVoucher?.VoucherNo || selectedVoucher?.VoucherID}</span>
                    </div>
                    <div className="vch_meta_item">
                      <span className="vch_meta_label">Voucher Date</span>
                      <span className="vch_meta_val">
                        {selectedVoucher?.VoucherDate
                          ? new Date(selectedVoucher.VoucherDate).toLocaleDateString("en-IN")
                          : "-"}
                      </span>
                    </div>
                    <div className="vch_meta_item">
                      <span className="vch_meta_label">Reference / Invoice</span>
                      <span className="vch_meta_val">{selectedVoucher?.ReferenceNo || "None"}</span>
                    </div>
                    {selectedVoucher?.CircularRefNo && (
                      <div className="vch_meta_item">
                        <span className="vch_meta_label">Linked Voucher</span>
                        <span className="vch_meta_val">Sales Voucher #{selectedVoucher.CircularRefNo}</span>
                      </div>
                    )}
                  </div>

                  {/* Double Entry Table */}
                  <table className="vch_detail_table">
                    <thead>
                      <tr>
                        <th style={{ width: "40px", textAlign: "center" }}>#</th>
                        <th style={{ width: "130px" }}>Head Code</th>
                        <th>Particulars / Ledger</th>
                        <th style={{ width: "120px", textAlign: "right" }}>Debit (₹)</th>
                        <th style={{ width: "120px", textAlign: "right" }}>Credit (₹)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {voucherDetails.map((row, idx) => (
                        <tr key={row.VDID || idx}>
                          <td style={{ textAlign: "center", color: "#94a3b8" }}>{idx + 1}</td>
                          <td style={{ fontWeight: 600, color: "#475569" }}>{row.HEAD_CODE}</td>
                          <td>{row.PARTICULAR}</td>
                          <td style={{ textAlign: "right", fontWeight: Number(row.DEBIT) > 0 ? 700 : 400 }}>
                            {Number(row.DEBIT) > 0 ? formatINR(row.DEBIT) : "-"}
                          </td>
                          <td style={{ textAlign: "right", fontWeight: Number(row.CREDIT) > 0 ? 700 : 400 }}>
                            {Number(row.CREDIT) > 0 ? formatINR(row.CREDIT) : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="3" style={{ textAlign: "right" }}>Total:</td>
                        <td style={{ textAlign: "right", color: "#064e48" }}>
                          ₹{formatINR(voucherDetails.reduce((s, r) => s + (Number(r.DEBIT) || 0), 0))}
                        </td>
                        <td style={{ textAlign: "right", color: "#064e48" }}>
                          ₹{formatINR(voucherDetails.reduce((s, r) => s + (Number(r.CREDIT) || 0), 0))}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </>
              )}
            </div>

            <div className="vch_modal_footer d-flex justify-content-between align-items-center">
              <button
                type="button"
                className="vch_btn_apply"
                onClick={() => {
                  const vId = selectedVoucher?.voucherId || selectedVoucher?.VoucherID;
                  setSelectedVoucher(null);
                  navigate(`/transactions/modify-voucher?voucherId=${vId}`);
                }}
                style={{ background: "#d97706", borderColor: "#b45309" }}
              >
                <i className="fa-solid fa-pen-to-square"></i> Modify Voucher in Transactions
              </button>
              <button
                type="button"
                className="vch_btn_close"
                onClick={() => setSelectedVoucher(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generate Voucher from Invoice Modal */}
      {generateModalOpen && (
        <div className="vch_modal_backdrop" onClick={() => setGenerateModalOpen(false)}>
          <div className="vch_modal_card" style={{ maxWidth: "460px" }} onClick={(e) => e.stopPropagation()}>
            <div className="vch_modal_header">
              <div className="vch_modal_title">
                <i className="fa-solid fa-wand-magic-sparkles" style={{ color: "var(--vch-accent)" }}></i>
                <span>Post Vouchers from Invoice</span>
              </div>
              <button
                type="button"
                className="vch_modal_close"
                onClick={() => setGenerateModalOpen(false)}
              >
                <i className="fa-solid fa-xmark"></i>
              </button>
            </div>

            <form onSubmit={handleGenerateFromInvoice}>
              <div className="vch_modal_body">
                <p style={{ fontSize: "12px", color: "#64748b", marginBottom: 14 }}>
                  Generates <strong>Sales Voucher</strong> (VType 5) and automatic <strong>Journal Voucher</strong> (VType 3 for market/vendor duty slips) by executing stored procedure <code>sp_GenerateVouchersFromInvoice</code>.
                </p>

                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: "block", fontSize: "11.5px", fontWeight: 700, color: "#334155", marginBottom: 4 }}>
                    Invoice ID:
                  </label>
                  <input
                    type="number"
                    required
                    placeholder="Enter Invoice ID (e.g. 8463)"
                    className="vch_search_input"
                    style={{ paddingLeft: "10px", width: "100%", height: "34px", fontSize: "13px" }}
                    value={invoiceIdInput}
                    onChange={(e) => setInvoiceIdInput(e.target.value)}
                    autoFocus
                  />
                </div>
              </div>

              <div className="vch_modal_footer">
                <button
                  type="button"
                  className="vch_btn_close"
                  onClick={() => setGenerateModalOpen(false)}
                  disabled={isGenerating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="vch_btn_generate"
                  disabled={isGenerating}
                >
                  {isGenerating ? (
                    <>
                      <i className="fa-solid fa-spinner fa-spin"></i> Generating...
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-check"></i> Generate Vouchers
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
